import React from 'react';
import styled from 'styled-components';
import {Button, FlexRow, Icon, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useAgentDrawerContext} from '../../../forecast-app/shared/contexts/AgentDrawerContext';
import {useReportBuilder} from '../ReportBuilderContext';

const tokens = tokensForThemeProvider.sky.sys;
const compTokens = tokensForThemeProvider.sky.comp;

/** Left accent bar color/width per the design (a 12px-wide solid blue bar flush on the
 * banner's left edge). `comp.alerts.color.bar.information` is the DS token for exactly this
 * "informational accent bar" affordance — note `comp` is a sibling of `sys` under `sky`, not
 * nested inside `sys`. The fill uses the matching `comp.alerts.color.background.information`
 * token (light-blue informational alert background per the design) — NOT `sys.color.background.accent`
 * (that resolves to the Sky theme's bright yellow accent, which is wrong for this "info" banner). */
/** the design's banner container has asymmetric horizontal
 * padding: `paddingLeft=24 paddingRight=16 paddingTop=8 paddingBottom=8` — not the uniform 8/12
 * shorthand previously used here. */
const BannerWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 60px;
	padding: ${tokens.spacing['200']} ${tokens.spacing['400']} ${tokens.spacing['200']} ${tokens.spacing['600']};
	background: ${compTokens.alerts.color.background.information};
	border-left: 12px solid ${compTokens.alerts.color.bar.information};
	border-radius: 4px;
	/* the design: 'Toast' node: DROP_SHADOW blur 4, #232334 @ 12%, offset (0,2). */
	box-shadow: 0 2px 4px rgba(35, 35, 52, 0.12);
`;

/** The prompt text is rendered italic + quoted per the design. When the payload carries the originating
 * NL `prompt` (ReportBuilderContext `ReportIntentPayload.prompt`, populated by the agent) the banner
 * shows `Generated from your prompt "<prompt>"`; when absent it falls back to the static
 * `generated_from_prompt` copy. Only the prompt run is italicised, not the whole line. */
const ItalicPromptText = styled.span`
	font-style: italic;
	color: ${compTokens.alerts.color.text.information};
`;

/** Sparkle glyph substituted for the raw `✨` emoji per the design (screenshot shows a monochrome
 * sparkle-style icon glyph, not a colorful emoji character) — the DS `sparkle` icon is the closest
 * match in `icons.d.ts`. */
const SparkleIconWrapper = styled.span`
	display: inline-flex;
	align-items: center;
`;

/** Banner shown below the surface's top bar when the report was generated from an AI intent; the
 * caller renders this only for that origin, never for a standard report. "Edit Prompt" opens the
 * agent drawer via `useAgentDrawerContext()`, pre-filled with the original prompt so revising the
 * report starts from what was originally asked for rather than an empty chat. A report with no
 * stored prompt opens the chat untouched instead of clearing whatever the user had already typed
 * there. */
export function AIBanner(): JSX.Element {
	const {formatMessage} = useIntl();
	const {openDrawer, setInputValue} = useAgentDrawerContext();
	const {prompt} = useReportBuilder();
	const infoColor = compTokens.alerts.color.text.information;

	const editPrompt = () => {
		if (prompt) {
			setInputValue(prompt);
		}
		openDrawer();
	};

	return (
		<BannerWrapper data-cy={'ai-report-banner'}>
			<FlexRow alignItems={'center'} gap={'s'}>
				<SparkleIconWrapper>
					<Icon icon={'sparkle'} size={'s'} color={compTokens.alerts.color.semanticicon.information} />
				</SparkleIconWrapper>
				<Text color={infoColor}>
					{formatMessage({
						id: prompt
							? 'ai_report_builder.generated_from_prompt_label'
							: 'ai_report_builder.generated_from_prompt',
					})}
				</Text>
				{prompt ? <ItalicPromptText>&ldquo;{prompt}&rdquo;</ItalicPromptText> : null}
			</FlexRow>
			<Button emphasis={'low'} onPress={editPrompt} data-cy={'ai-report-edit-prompt-button'}>
				{formatMessage({id: 'ai_report_builder.edit_prompt'})}
			</Button>
		</BannerWrapper>
	);
}
